import { DealBankDetails } from "./BankDetails";
import { FileInterface } from "./Documents";
import { Investable } from "./Investable";
import { Label } from "./Label";

export interface Logo {
  id: string;
  name: string;
  url: string;
}

export type DealType =
  | "STARTUP"
  | "FUND"
  | "STARTUP_DEAL"
  | "EIS_FUND"
  | "REAL_ESTATE_DEAL"
  | "REAL_ESTATE_FUND"
  | "PRIVATE_DEAL";

export type DealStatus =
  | "OFFLINE"
  | "PRELAUNCH"
  | "LIVE_ALL"
  | "LIVE_MEMBERS"
  | "LIVE_PRIVATE"
  | "CLOSED";

export type IncomeDecisionType = "WITHDRAW" | "REINVEST" | "REALLOCATE";

export type SupportedIncomeDecisionType = {
  withdraw: boolean;
  reallocate: boolean;
  reinvest: boolean;
};

export interface DealExplorerConfig {
  enabled: boolean;
  annualisedReturn: DealExplorerLabel;
  risk: DealExplorerLabel;
  taxBenefits: DealExplorerLabel;
  distributionPaid: DealExplorerLabel;
  assetClass: DealExplorerLabel;
}

export interface DealExplorerLabel {
  value: string;
  prompts: readonly [string, string, string];
}

export interface Deal {
  id: string;
  name: string;
  description: string;
  fundraiseTarget: number | null;
  premoneyValuation: number | null;
  summary: string | null;
  videoUrl: string | null;
  status: DealStatus;
  minimumInvestment: number | null;
  maximumInvestment?: number | null;
  fundraisingCap?: number | null;
  discountCode: string | null;
  escrowProvider: string | null;
  thompsonTarazDealId: string | null;
  taxBenefit: string | null;
  expiryDate: string | null;
  launchDate: string | null;
  created_at: string;
  updated_at: string;
  investable: Investable | null;
  newDocuments: FileInterface[];
  shariaComplianceDocuments: FileInterface[];
  shariaComplianceSummary: string | null;
  webinarEmbedLink?: string | null;
  newInvestorDocuments: FileInterface[];
  dealBankDetails: DealBankDetails[];
  pubbleAppId?: string | null;
  labels: Label[];
  isReturnsCalculatorEnabled?: boolean | null;
  returnsCalculatorDisclaimer?: string | null;
  minInvestmentPeriod?: number | null;
  maxInvestmentPeriod?: number | null;
  fixedInvestmentDuration?: number | null;
  avgReturnsMultiple?: number | null;
  expectedDeploymentDate?: string | null;
  isIFISADeal?: boolean | null;
  supportedIncomeDecisions?: SupportedIncomeDecisionType | null;
  defaultIncomeDecision?: IncomeDecisionType | null;
  allowInvestorToModifyIncomeDecision?: boolean | null;
  liquidationNextWindowStartDate: Date | null;
  usesPeriods?: boolean;
  periodDealConfig?: PeriodDealConfig | null;
  activePeriodRate?: DealActivePeriodRate | null;
  hasCommitment: boolean;
  enablePortfolioV1_5: boolean;
  dealExplorerConfig?: DealExplorerConfig | null;
  dealImage?: FileInterface | null;
  metadata?: Record<string, unknown> | null;
  nextDistributionDate?: string | null;
  subHeading?: string | null;
  descriptionBlocks?: DealDescriptionBlock[] | null;
  feeTypes?: DealFeeType[];
}

/** Mirrors invest-api periods.types PeriodDealConfig (notice in quarters). */
export interface PeriodDealConfig {
  noticePeriods: number;
  boosterUplift: number | null;
  memberUplift: number | null;
}

/** Mirrors invest-api periods.types DealActivePeriodRate (fractions, e.g. 0.075). */
export interface DealActivePeriodRate {
  standardRate: number;
  exitRate: number;
  noticePeriods: number;
  noticeMonths: number;
  isMemberRate: boolean;
}

/** Mirrors invest-api periods.types PeriodDealConfig (notice in quarters). */
export interface PeriodDealConfig {
  noticePeriods: number;
  boosterUplift: number | null;
  memberUplift: number | null;
}

/** Mirrors invest-api periods.types DealActivePeriodRate (fractions, e.g. 0.075). */
export interface DealActivePeriodRate {
  standardRate: number;
  exitRate: number;
  noticePeriods: number;
  noticeMonths: number;
  isMemberRate: boolean;
}

/**
 * One rung of a fund's rate ladder: a tenor deal's active rate plus the deal
 * facts needed to decide how to show it. Ordered by notice length; OFFLINE
 * tenors never appear. Mirrors invest-api InvestablePeriodRate.
 */
export interface InvestablePeriodRate extends DealActivePeriodRate {
  dealId: string;
  status: DealStatus;
  isIfIsaEligible: boolean;
}

/**
 * An overview block. `type` is optional and absent means "richText", so blocks
 * authored before typed blocks existed keep working. Renderers must treat an
 * unrecognised type as richText rather than assuming a shape.
 * Mirrors invest-api drizzle.types DealDescriptionBlock.
 */
export type DealDescriptionBlock =
  | DealRichTextBlock
  | DealKeyValueBlock
  | DealStatsBlock;

/**
 * Which tab a block renders on. Absent means "description", where every block
 * rendered before placement existed.
 */
export type DealBlockPlacement = "description" | "overview";

export interface DealRichTextBlock {
  placement?: DealBlockPlacement;
  type?: "richText";
  title: string;
  description: string;
  imageURL?: string | null;
}

/** Label/value rows — risk and structure, fund entities, asset allocation. */
export interface DealKeyValueBlock {
  placement?: DealBlockPlacement;
  type: "keyValue";
  title: string;
  rows: DealKeyValueRow[];
  note?: string;
  callout?: DealBlockCallout;
}

export interface DealKeyValueRow {
  label: string;
  value: string;
  emphasis?: boolean;
}

export interface DealBlockCallout {
  text: string;
  subtext?: string;
}

/** Headline figures with an optional dated history — e.g. a track record. */
export interface DealStatsBlock {
  placement?: DealBlockPlacement;
  type: "stats";
  title: string;
  headline?: string;
  stats: DealBlockStat[];
  entries?: DealStatsEntry[];
  footerStat?: DealBlockStat;
}

export interface DealBlockStat {
  /** Editorial, not computed — e.g. "£52m+". */
  value: string;
  label: string;
}

export interface DealStatsEntry {
  label: string;
  value: string;
  confirmed?: boolean;
}

/** Mirrors invest-api feeType.types FeeType (rates are fractions, e.g. 0.02). */
export interface DealFeeType {
  id: string;
  name: string;
  description: string;
  base: string;
  rateConfig?: Record<string, unknown> | null;
  periodicity?: string | null;
  feeFreeDuration?: number | null;
  feeDuration?: number | null;
  defaultRate?: number | null;
  category: string;
  collectionPoint?: string | null;
  created_at: string;
  updated_at: string;
}

export type CapacityState = "OPEN" | "LIMITED" | "FULL";

export interface DealFundraisingCapacity {
  fundraisingCap: number | null;
  remainingCapacity: number | null;
  capacityState: CapacityState | null;
}

/** Map of dealId -> capacity, returned by GET /deals/fundraising-capacity. */
export type DealsFundraisingCapacity = Record<string, DealFundraisingCapacity>;

export interface GetDealsParams {
  statuses?: string[];
  sortBy?: string;
  pageSize?: number;
}

export interface ReturnCalculatorInput {
  dealId: string;
  amount: number;
  investmentDuration: number;
}

export interface ReturnCalculatorResponse {
  amountToDeployIfNonMember: number;
  amountToDeployIfMember: number;
  returnsExclFeesMember: number;
  returnsExclFeesNonMember: number;
}

export interface ReturnCalculatorV2Request {
  dealId: string;
  amount: number;
  investmentDuration: number;
  isMember: boolean;
}

export interface ReturnCalculatorV2Response {
  returnsExclFees: number;
  feeAmount: number;
  totalAmount: number;
}

export enum ContributionFrequency {
  NONE = "NONE",
  MONTHLY = "MONTHLY",
  QUARTERLY = "QUARTERLY",
}

export enum DistributionFrequency {
  QUARTERLY = "QUARTERLY",
  SEMI_ANNUAL = "SEMI_ANNUAL",
  ANNUAL = "ANNUAL",
}

export type CollectionPoint =
  | "DEPLOYMENT"
  | "INCOME_DISTRIBUTION"
  | "LIQUIDATION"
  | "SELLER_SHARE_SALE"
  | "BUYER_SHARE_SALE";

export type InvestableTypeEnum = "INCOME" | "SHARE";

export interface ContributionPlan {
  amount: number;
  frequency: ContributionFrequency;
}

export interface ProjectReturnsRequest {
  dealId: string;
  years: number;
  initialInvestment: number;
  reinvestDistributions: boolean;
  contributionPlan: ContributionPlan;
  isMember: boolean;
}

export interface FeePeriodBreakdown {
  period: string;
  amount: number;
  rate?: number;
}

export interface FeeBreakdownItem {
  feeTypeId: string;
  name: string;
  description: string;
  collectionPoint: CollectionPoint;
  amount: number;
  occurrences: number;
  breakdownByPeriod?: FeePeriodBreakdown[];
}

export interface FeeSummary {
  total: number;
  breakdown: FeeBreakdownItem[];
}

export interface QuarterSnapshot {
  quarterIndex: number;
  year: number;
  totalValue: number;
  investedCapital: number;
  accruedReturn: number;
  totalFeesToDate: number;
  distributedCashToDate: number;
  sharePrice?: number;
  sharesHeld?: number;
}

export interface PrincipalBreakdown {
  transferredAmount: number;
  deploymentFees: number;
  initialInvestmentNet: number;
  totalContributionGross: number;
  totalContributionNet: number;
}

export interface ReturnsBreakdown {
  totalAccrued: number;
  reinvested: number;
  paidOut: number;
  grossReturns: number;
  netReturns: number;
  dividendsEarned?: number;
  yieldOverPeriod?: number;
  principalReturned: number;
  contributionsReturned: number;
}

export interface YearBreakdownResponse {
  year: number;
  endValue: number;
  netValue: number;
  returnsEarned: number;
  feesPaid: number;
  totalFeesToDate: number;
  cashDistributed: number;
  contributionsMade: number;
  sharesHeld?: number;
  sharePrice?: number;
  priceAppreciationPercent?: number;
}

export interface ProjectionSummary {
  finalValue: number;
  totalPayouts: number;
  grossValue: number;
  netGain: number;
  costBasis: number;
  projectedNetReturn: number;
  quarters: number;
  reinvestedTotal: number;
  contributionPlan: ContributionPlan;
  transferredAmount: number;
  totalReturnPercentage: number;
  annualizedReturn: number;
  annualReturnRate: number;
  memberDiscountSavings?: number;
}

export interface ReturnProjectionResponse {
  dealType: InvestableTypeEnum;
  timeline: QuarterSnapshot[];
  yearlyBreakdown: YearBreakdownResponse[];
  fees: FeeSummary;
  principal: PrincipalBreakdown;
  returns: ReturnsBreakdown;
  summary: ProjectionSummary;
}
